/**
 * AgreementReviewForm — the seven-field editable review screen shown between
 * "Generate" and "Send to DocuSign". Fields deliberately kept narrow (per
 * 2026-09 product decision): only the values that occasionally change post-
 * approval. Everything else is derived from application/underwriting and is
 * not editable by staff.
 *
 * UX invariants:
 *   * Live values pre-populate — never blank on first render.
 *   * A blank field is treated as "leave live value alone" (backend applies
 *     no override).
 *   * A field whose value differs from the live database value is visually
 *     highlighted (amber left-border, "Edited" chip) and gains a per-field
 *     Reset button. This makes it trivial to spot and undo an accidental
 *     change during review.
 */
import React, { useEffect, useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Save, RotateCcw, Undo2 } from "lucide-react";

const FIELDS = [
  { key: "client_name", label: "Client name", section: "Plaintiff" },
  { key: "client_address_line1", label: "Client street address", section: "Plaintiff" },
  { key: "client_city", label: "City", section: "Plaintiff", compact: true },
  { key: "client_state", label: "State", section: "Plaintiff", compact: true, maxLen: 2 },
  { key: "client_zip", label: "ZIP", section: "Plaintiff", compact: true, maxLen: 10 },
  { key: "attorney_name", label: "Attorney name", section: "Attorney" },
  { key: "attorney_firm", label: "Attorney firm", section: "Attorney" },
  { key: "attorney_address_line1", label: "Attorney street address", section: "Attorney" },
  { key: "attorney_city", label: "City", section: "Attorney", compact: true },
  { key: "attorney_state", label: "State", section: "Attorney", compact: true, maxLen: 2 },
  { key: "attorney_zip", label: "ZIP", section: "Attorney", compact: true, maxLen: 10 },
  { key: "funding_date", label: "Funding date (YYYY-MM-DD)", section: "Funding" },
  { key: "loan_amount", label: "Loan amount (USD)", section: "Funding" },
];

function _splitCsz(csz) {
  const m = /^\s*(.+?)\s*,\s*([A-Za-z]{2})\s+(\S+)\s*$/.exec(csz || "");
  if (!m) return { city: "", state: "", zip: "" };
  return { city: m[1], state: m[2], zip: m[3] };
}

// Build the "live" baseline that Reset-to-live buttons snap back to.
function _liveBaselineFromContext(pc) {
  if (!pc) return {};
  const csz = _splitCsz(pc.client_city_state_zip);
  const acsz = _splitCsz(pc.attorney_city_state_zip);
  return {
    client_name: pc.client_name || "",
    client_address_line1: pc.client_address_line1 || "",
    client_city: csz.city,
    client_state: csz.state,
    client_zip: csz.zip,
    attorney_name: pc.attorney_name || "",
    attorney_firm: pc.attorney_firm || "",
    attorney_address_line1: pc.attorney_address || "",
    attorney_city: acsz.city,
    attorney_state: acsz.state,
    attorney_zip: acsz.zip,
    funding_date: (pc.funding_date || "").slice(0, 10),
    loan_amount: pc.loan_amount || "",
  };
}

export default function AgreementReviewForm({ previewContext, initialForm, onSave, onReset, saving }) {
  const [form, setForm] = useState({});
  const live = _liveBaselineFromContext(previewContext);

  useEffect(() => {
    // Seed the form with live values, then layer any prior review edits on
    // top so the admin doesn't lose in-flight changes on remount.
    setForm({ ...live, ...(initialForm || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewContext, initialForm]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const resetField = (k) => setForm(f => ({ ...f, [k]: live[k] || "" }));
  const isEdited = (k) => (form[k] || "") !== (live[k] || "");
  const editedCount = FIELDS.filter(f => isEdited(f.key)).length;

  const sections = ["Plaintiff", "Attorney", "Funding"];

  return (
    <div data-testid="agreement-review-form" className="space-y-5">
      {editedCount > 0 && (
        <div className="inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs font-medium bg-amber-50 border border-amber-200 text-amber-800"
             data-testid="review-edited-count">
          {editedCount} field{editedCount === 1 ? "" : "s"} differ from live data
        </div>
      )}

      {sections.map(section => (
        <div key={section}>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">
            {section}
          </div>
          <div className="grid grid-cols-6 gap-3">
            {FIELDS.filter(f => f.section === section).map(f => {
              const edited = isEdited(f.key);
              const liveVal = live[f.key] || "";
              return (
                <div key={f.key} className={f.compact ? "col-span-2" : "col-span-6"}>
                  <div className="flex items-baseline justify-between">
                    <Label
                      className={"text-xs " + (edited ? "text-amber-800 font-medium" : "text-[#6B7280]")}
                    >
                      {f.label}
                      {edited && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 font-semibold"
                              data-testid={`review-field-${f.key}-edited-chip`}>
                          Edited
                        </span>
                      )}
                    </Label>
                    {edited && (
                      <button
                        type="button"
                        onClick={() => resetField(f.key)}
                        data-testid={`review-field-${f.key}-reset`}
                        title={`Reset to "${liveVal || "(empty)"}"`}
                        className="inline-flex items-center gap-1 text-[10px] text-amber-800 hover:text-amber-900 hover:underline"
                      >
                        <Undo2 className="w-3 h-3" /> reset
                      </button>
                    )}
                  </div>
                  <Input
                    value={form[f.key] || ""}
                    onChange={e => update(f.key, e.target.value)}
                    maxLength={f.maxLen}
                    data-testid={`review-field-${f.key}`}
                    className={
                      "mt-1 h-9 text-sm transition-colors " +
                      (edited
                        ? "border-l-4 border-l-amber-400 bg-amber-50/60 focus:bg-white"
                        : "")
                    }
                  />
                  {edited && (
                    <div className="text-[10px] text-[#6B7280] mt-1 font-mono truncate"
                         data-testid={`review-field-${f.key}-live`}>
                      live: {liveVal || <em className="text-[#D1D5DB]">(empty)</em>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex items-center gap-2 pt-2 border-t border-[#E5E7EB]">
        <Button
          onClick={() => onSave(form)}
          disabled={saving || editedCount === 0}
          data-testid="review-save"
          className="bg-[#0066FF] hover:bg-[#0052CC] text-white disabled:opacity-40"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Regenerating…" : `Save ${editedCount || ""} change${editedCount === 1 ? "" : "s"} & regenerate`}
        </Button>
        <Button
          onClick={onReset}
          disabled={saving}
          variant="outline"
          data-testid="review-reset-all"
          className="border-[#E5E7EB]"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset all to live data
        </Button>
        <div className="text-xs text-[#9CA3AF] ml-auto">
          Blank fields leave the current live value unchanged.
        </div>
      </div>
    </div>
  );
}
